import React, { Fragment, useState, useEffect, useMemo } from 'react';
import styled, { useTheme, css, keyframes } from 'styled-components';
import PropTypes from 'prop-types';
import faker from '@faker-js/faker';
import { color } from 'styled-system';

import { Text, Box, Border, Flex, List, SvgIcon } from 'Components/Base';
import { get as getTheme } from 'Components/Base/constants';
import { useClassroomCourseData } from 'Domain/Classroom';
import { getDurationWithoutHour, localeDate } from 'Util/datetime';

const SubSectionNumber = styled(Border)`
  min-width: 40px;
  border-radius: 16px;
  padding: 2px 8px 2px 8px;
  font-weight: ${getTheme('fontWeights.light') ?? 300};
  text-align: center;
`;

const CourseSubsectionContainer = styled(Box)`
  cursor: pointer;
`;

const CourseSubsectionInnerWrapper = styled(Flex)`
  border-radius: 16px;
`;

const StatusIndicator = styled.span`
  ${color}
  margin-right: 8px;
  height: 8px;
  width: 8px;
  border-radius: 50%;
  margin-top: 8px;
`;

const ContentText = (props) => (
  <Text
    textStyle={'body2'}
    fontFamily={'content'}
    fontWeight={'normal'}
    color="inherit"
    {...props}
  />
);

const CourseTOCSectionTitle = styled(Box)`
  cursor: pointer;

  ${({ hasSubsectionActive }) =>
    hasSubsectionActive
      ? css`
          border-left: 4px solid ${getTheme('colors.primary')};
        `
      : css`
          border-left: 4px solid ${getTheme('colors.transparent')};
        `}
`;

const ContentTypeWrapper = styled((props) => (
  <Flex
    flex={'0 0 auto'}
    width={'24px'}
    height={'24px'}
    justifyContent={'center'}
    alignItems={'center'}
    {...props}
  />
))`
  overflow: hidden;
  border-radius: 100%;
`;

const ContentTypeIcon = styled(SvgIcon)`
  ${({ active }) =>
    active &&
    css`
      & > * {
        fill: ${getTheme('colors.white')};
      }
    `}
`;

const SubSectionArrowRotation = (subsectionState) => keyframes`
  0% {
    transform: ${subsectionState === 'active' ? 'rotate(180deg)' : 'rotate(0)'}
  };

  100% {
    transform: ${subsectionState === 'active' ? 'rotate(0)' : 'rotate(180deg)'}
  }
`;

const SubSectionArrow = styled(SvgIcon)`
  color: ${getTheme('colors.primary')};
  flex-shrink: 0;

  ${({ subsectionState }) =>
    subsectionState === 'inactive'
      ? css`
          transform: rotate(180deg);
        `
      : css`
          transform: rotate(0);
        `}

  animation: ${({ subsectionState }) =>
    SubSectionArrowRotation(subsectionState)} 300ms cubic-bezier(.13,.43,.93,.71);
`;

const getSubSectionColor = (complete, isPreviewSubsectionInactive, active) => {
  if (active) {
    return {
      color: 'activeSubsectionSidebarText',
      bg: 'activeSubsectionSidebarBackground',
    };
  }
  if (complete) {
    return { bg: 'transparent', color: 'completedSubsectionSidebarText' };
  }
  if (isPreviewSubsectionInactive) {
    return { bg: 'notiGrey.grey3', color: 'sidebarSecondaryText' };
  }
  return {
    color: 'sidebarSecondaryText',
    bg: 'transparent',
  };
};

const getContentTypeBackground = (isPreviewSubsectionInactive, active) => {
  if (active) {
    return { bg: 'primaryLight' };
  }
  if (isPreviewSubsectionInactive) {
    return { bg: 'greyscale.grey4' };
  }
  return { bg: 'notiGrey.grey3' };
};

const contentTypeIcon = (contentType) => {
  switch (contentType) {
    case 'VIDEO':
    case 'VIDEO_SLIDE':
    case 'YOUTUBE':
      return SvgIcon.ICON_LIST.VIDEO_PLAYER_TYPE;
    case 'LIVECODING':
      return SvgIcon.ICON_LIST.LIVECODING_TYPE;
    case 'QUIZ':
      return SvgIcon.ICON_LIST.GRADED_EXAM_TYPE;
    case 'QUIZ_SIMPLE':
      return SvgIcon.ICON_LIST.QUIZ_TYPE;
    case 'NOTEBOARD':
      return SvgIcon.ICON_LIST.NOTEBOARD_TYPE;
    case 'EBOOK':
      return SvgIcon.ICON_LIST.PDF_TYPE;
    case 'ARTICLE':
      return SvgIcon.ICON_LIST.ARTICLE_TYPE;
    case 'ASSIGNMENT':
      return SvgIcon.ICON_LIST.ASSIGNMENT_TYPE;
    default:
      return null;
  }
};

const CourseSection = ({ section, onSubsectionClick, defaultExpanded, name, ...props }) => {
  const { isClassroomTrialMode } = useClassroomCourseData();
  const { colors, space } = useTheme();
  const [isSubsectionOpen, setIsSubsectionOpen] = useState(defaultExpanded || false);
  const {
    subsections,
    title,
    totalTime,
    numberAllSubsection,
    numberComplatedSubsection,
    releasedAt,
  } = section;

  useEffect(() => {
    const indexOfActiveSubsection = subsections.findIndex((s) => s.active === true);
    if (indexOfActiveSubsection !== -1) {
      setIsSubsectionOpen(true);
    }
  }, []);

  const handleToggleSubsectionOpen = () => {
    setIsSubsectionOpen(!isSubsectionOpen);
  };

  const handleSubsectionClick = ({ subsection, section }) => {
    setIsSubsectionOpen(true);
    onSubsectionClick({ subsection, section });
  };

  const hasSubsectionActive = useMemo(() => {
    const subsection = subsections.find((subsection) => subsection?.active === true);
    if (subsection) return true;
    return false;
  }, [subsections]);

  const isSubSectionCompleted = () => numberComplatedSubsection / numberAllSubsection === 1.0;

  return (
    <Box
      bg={'transparent'}
      style={{
        borderBottom: `1px solid ${colors.sidebarSecodaryDivider}`,
      }}
    >
      <CourseTOCSectionTitle
        data-testid={`${name}-tab`}
        pl={[3, null, '28px']}
        pr={[4, null, 7]}
        py={4}
        onClick={handleToggleSubsectionOpen}
        hasSubsectionActive={hasSubsectionActive}
      >
        <Flex justifyContent={'space-between'} width={'100%'}>
          <ContentText fontWeight="medium">{title}</ContentText>
          <SubSectionArrow
            size={24}
            subsectionState={isSubsectionOpen ? 'active' : 'inactive'}
            icon={SvgIcon.ICON_LIST.ARROW_UP}
          />
        </Flex>
        <Flex alignItems={'center'} letterSpacing={'0.5'} fontSize={0} lineHeight={'1.5'}>
          <SubSectionNumber
            marginRight={'10px'}
            border={!isSubSectionCompleted() && 1}
            borderColor={!isSubSectionCompleted() && 'primary'}
            backgroundColor={isSubSectionCompleted() && 'primary'}
            color={isSubSectionCompleted() && 'completedSubsectionNumberSidebarText'}
          >
            {numberComplatedSubsection} / {numberAllSubsection}
          </SubSectionNumber>
          <Text color={'#CFCFCF'} marginRight={'10px'}>
            |
          </Text>
          <Text textStyle={'caption'} fontWeight={'normal'}>
            {getDurationWithoutHour(totalTime, 'EN')}
          </Text>
          {releasedAt && (
            <ContentText fontSize={0} color={'greyscale.grey3'} ml="auto" lineHeight={0}>
              เริ่มเรียน {localeDate(releasedAt, 'th', 'DD MMM BB')}
            </ContentText>
          )}
        </Flex>
      </CourseTOCSectionTitle>

      {isSubsectionOpen && (
        <List
          items={subsections}
          renderItem={(subsection, index) => {
            const {
              active: isCurrentActiveSubsection,
              complete,
              failed,
              submitted,
              title,
              id,
              referId,
              contentType,
              canPreview: canPreviewSubsection,
              meta: { videoTime },
            } = subsection;
            const _isPreviewSubsectionInactive = isClassroomTrialMode && !canPreviewSubsection;
            const { color, bg } = getSubSectionColor(
              complete,
              _isPreviewSubsectionInactive,
              isCurrentActiveSubsection
            );
            const { bg: contentTypeBg } = getContentTypeBackground(
              _isPreviewSubsectionInactive,
              isCurrentActiveSubsection
            );
            return (
              <CourseSubsectionContainer
                key={id}
                data-testid={`${name}-subsection-${index + 1}-tab`}
                color={color}
                active={isCurrentActiveSubsection}
                px={[4, null, 7]}
                py={isCurrentActiveSubsection ? 0 : 4}
                bg={bg}
                onClick={() => handleSubsectionClick({ subsection, section })}
              >
                <CourseSubsectionInnerWrapper
                  py={isCurrentActiveSubsection ? 4 : 0}
                  active={isCurrentActiveSubsection}
                >
                  {complete && (
                    <SvgIcon
                      icon={SvgIcon.ICON_LIST.CHECK_CIRCLE}
                      size={18}
                      pt={'2px'}
                      mr={4}
                      color={'success'}
                      style={{ flexShrink: 0, height: '24px' }}
                    />
                  )}
                  {failed && <StatusIndicator backgroundColor="danger" />}
                  {submitted && <StatusIndicator backgroundColor="warning" />}

                  {_isPreviewSubsectionInactive && (
                    <SvgIcon
                      icon={SvgIcon.ICON_LIST.LOCKED}
                      size={24}
                      mr={4}
                      style={{ flexShrink: 0, height: '24px' }}
                    />
                  )}

                  <ContentText
                    style={{ flexGrow: 1 }}
                    color={isCurrentActiveSubsection ? 'text' : 'greyscale.grey3'}
                    pt={1}
                  >
                    {title}
                  </ContentText>

                  <ContentText
                    fontSize={0}
                    ml={3}
                    mr={2}
                    pt={'5px'}
                    textAlign={'right'}
                    color={'greyscale.grey3'}
                    style={{ flex: '1 0 auto' }}
                  >
                    {getDurationWithoutHour(videoTime, 'EN')}
                  </ContentText>

                  <ContentTypeWrapper bg={contentTypeBg}>
                    <ContentTypeIcon
                      icon={contentTypeIcon(contentType)}
                      size={24}
                      active={isCurrentActiveSubsection}
                    />
                  </ContentTypeWrapper>
                </CourseSubsectionInnerWrapper>
              </CourseSubsectionContainer>
            );
          }}
          columns={1}
          childSpacing={'0px'}
          rowSpacing={space[4]}
        />
      )}
    </Box>
  );
};

const TocWithContentTypeIcon = ({ sections = [], onSubsectionClick, ...props }) => {
  const { isClassroomTrialMode } = useClassroomCourseData();
  return (
    <Fragment>
      {sections.map(({ subSections = [], ...section }, index) => {
        const sectionWithIndex = {
          subsections: subSections.map(({ number, ...subSection }) => ({
            subsectionIndex: number,
            ...subSection,
          })),
          ...section,
        };
        return (
          <CourseSection
            key={section.id}
            name={`toc-with-content-type-icon-section-${index + 1}`}
            section={sectionWithIndex}
            onSubsectionClick={onSubsectionClick}
            defaultExpanded={isClassroomTrialMode}
            {...props}
          />
        );
      })}
    </Fragment>
  );
};

CourseSection.propTypes = {
  name: PropTypes.string,
};

CourseSection.defaultProps = {
  name: faker.random.uuid(),
};

export default TocWithContentTypeIcon;
